'use client';

import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ptBR } from 'date-fns/locale';
import { forwardRef, HTMLAttributes, useImperativeHandle, useState } from 'react';

interface DateRangePickerProps extends HTMLAttributes<HTMLDivElement> {
    onDateChange: (startDate: string | undefined, endDate: string | undefined) => void;
}

export interface DateRangePickerRef {
    clear: () => void;
}

export const DateRangePicker = forwardRef<DateRangePickerRef, DateRangePickerProps>(({
    className,
    onDateChange,
}, ref) => {
    const [date, setDate] = useState<DateRange | undefined>(undefined);
    const [tempDate, setTempDate] = useState<DateRange | undefined>(undefined);
    const [open, setOpen] = useState(false);

    const handleApply = () => {
        setDate(tempDate);
        if (!tempDate) {
            onDateChange('', '');
            return;
        }
        const startDate = tempDate.from ? format(tempDate.from, 'yyyy-MM-dd') : '';
        const endDate = tempDate.to ? format(tempDate.to, 'yyyy-MM-dd') : '';
        onDateChange(startDate, endDate);
        setOpen(false);
    };

    const handleClear = () => {
        setDate(undefined);
        setTempDate(undefined);
        onDateChange('', '');
        setOpen(false);
    };

    useImperativeHandle(ref, () => ({
        clear: handleClear
    }));

    return (
        <div className={cn('grid gap-2', className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id="date"
                        variant={'outline'}
                        className={cn(
                            'h-8 w-auto justify-start text-left font-normal text-sm',
                            !date && 'text-muted-foreground'
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date?.from ? (
                            date.to ? (
                                <>
                                    {format(date.from, 'LLL dd, y', { locale: ptBR })} - {format(date.to, 'LLL dd, y', { locale: ptBR })}
                                </>
                            ) : (
                                format(date.from, 'LLL dd, y', { locale: ptBR })
                            )
                        ) : (
                            <span>Selecione um intervalo de datas</span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        initialFocus
                        mode="range"
                        locale={ptBR}
                        defaultMonth={tempDate?.from}
                        selected={tempDate}
                        onSelect={setTempDate}
                        numberOfMonths={2}
                        disabled={{ after: new Date() }}
                    />
                    <div className="border-t bg-white p-3 sm:p-4 mt-auto shrink-0">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                            <p className="text-xs text-gray-500 order-2 sm:order-1">Clique nas datas para selecionar</p>
                            <div className="flex gap-2 w-full sm:w-auto order-1 sm:order-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleClear}
                                >
                                    Limpar
                                </Button>
                                <Button
                                    size="sm"
                                    className="h-7 px-3 text-xs flex-1 sm:flex-none"
                                    onClick={handleApply}
                                >
                                    Aplicar
                                </Button>
                            </div>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
});
